/**
 * The caption for a strict backup success rate — Spec 270.
 *
 * 🛑 WHY THIS IS A SHARED FUNCTION AND NOT AN INLINE TEMPLATE STRING.
 *
 * The strict rate (Spec 214) is Completed ÷ (Completed + Partial + Failed + Cancelled).
 * Prod on 2026-08-13 rendered:
 *
 *      Backup Success   50%
 *      6 done · 0 failed today
 *
 * Both halves were true — 6 Completed, 6 Partial, so 6/12 = 50% — and the tile was
 * unreadable, because the only term that moved the denominator was the one term omitted.
 * Worse, the two halves came from DIFFERENT SOURCES: the caption from standalone SQL
 * subqueries keyed on `completed_at`, the percentage from a separate `today` bucket. They
 * could disagree while each was individually correct.
 *
 * Building the caption from the same bucket the rate divides makes that drift impossible,
 * and putting it here means the assertion below is against shipped code rather than a copy
 * of it — the difference between a test and a decoration.
 */
export interface BackupOutcomeBucket {
  completed: number;
  partial: number;
  failed: number;
  cancelled: number;
}

/**
 * Names every non-zero term of the rate's denominator, then states the denominator itself,
 * so the percentage above it can be checked by eye. Zero terms are dropped so a clean day
 * stays terse; a non-zero `partial` is never dropped, which is the entire point.
 *
 * `null` (or an all-zero bucket) yields "no runs finished today" rather than an outcome —
 * Spec 214 makes the rate NULL when nothing finished, never 100% and never 0%.
 */
export function backupSuccessSubtext(today: BackupOutcomeBucket | null | undefined): string {
  if (!today) return 'no runs finished today';
  const finished = today.completed + today.partial + today.failed + today.cancelled;
  if (finished === 0) return 'no runs finished today';
  const parts = [`${today.completed} completed`];
  if (today.partial > 0) parts.push(`${today.partial} partial`);
  if (today.failed > 0) parts.push(`${today.failed} failed`);
  if (today.cancelled > 0) parts.push(`${today.cancelled} cancelled`);
  return `${parts.join(' · ')} — of ${finished} finished today`;
}

/**
 * The canonical strict rate, exported alongside the caption so a test can assert the two
 * agree. Refused runs are deliberately outside the denominator: a refusal is a readiness
 * problem, not a backup outcome (Spec 214 FR-012).
 */
export function strictSuccessRatePct(today: BackupOutcomeBucket | null | undefined): number | null {
  if (!today) return null;
  const finished = today.completed + today.partial + today.failed + today.cancelled;
  return finished === 0 ? null : Math.round((today.completed / finished) * 100);
}
